import { apiRequest } from '@/domains/api';
import type {
  IBankQuestionRow,
  IBankQuestionWrite,
  ICreateOptionListVersion,
  ICreateSurvey,
  ICreateVersion,
  IDefinitionPreview,
  IDefinitionRule,
  IDefinitionSection,
  IDefinitionSurvey,
  IDefinitionUsage,
  IDefinitionVersion,
  IOptionListRow,
  IOptionListItemWrite,
  IOptionListVersionDetail,
  IPublicationResult,
  IRuleEffectWrite,
  IRuleWrite,
  ISectionWrite,
  ISurveyPath,
  IUsageWrite,
  IValidationResult,
  IVersionDocument,
  IVocabulary
} from './definitions-contracts';

/**
 * survey-api's definition authoring surface (SR-123) — all 45 routes, under
 * `definitions.author` except publish and retire, which are `definitions.publish`.
 *
 * ## `If-Match` is not optional
 *
 * Every mutation below a version quotes the draft's `revision` back. survey-api made the
 * header **required**: a missing one is 428, a stale one 409. So every such function here
 * takes a `revision` and there is no overload that omits it — a signature that let a caller
 * forget would be the silent-lost-edit this design exists to prevent.
 *
 * The number is not incremented locally after a write. It moves on the server on **every**
 * successful mutation, and a caller that guessed the next value would be right until the day
 * a write moved it by two. Callers re-read the document instead, which they need to do anyway
 * because a write can change more than it was given (a reorder renumbers every sibling).
 *
 * ## Whole-block writes
 *
 * The `PUT`s here replace, they do not patch: a field absent from the body is a cleared
 * column. The types in `definitions-contracts.ts` are shaped to make that hard to get wrong —
 * every member present and explicitly nullable — and survey-api guarantees a read document is
 * a legal write body for paths, questions, sections, rules and effects, so the natural editor
 * flow (fetch, change one field, send it all back) is also the correct one.
 */

const BASE = '/api/v1/admin/definitions';

/** The precondition header, spelled once. Weak tags are accepted by the server; this sends
 *  the strong form the server's own `ETag` uses. */
function ifMatch(revision: number): Record<string, string> {
  return { 'If-Match': `"${revision}"` };
}

/** `?code=` is a **code prefix**, case-sensitive — verified against a running server, where
 *  `?code=fa.` matches 141 questions and `?code=ai_adoption` matches none. It is not a
 *  search: a caller wanting to match a label filters what it fetched. */
function codePrefix(prefix?: string): string {
  return prefix ? `?code=${encodeURIComponent(prefix)}` : '';
}

/**
 * The vocabulary, held for the life of the page.
 *
 * It is the one read on this surface that genuinely cannot go stale: survey-api builds it
 * from code registries with **no database behind it**, so it changes on a deploy and at no
 * other moment — a fact it states about itself by carrying a `vocabVersion` and no revision.
 * Without this, the question bank refetched it on every visit and the version editor on every
 * open, for a document that was identical every time.
 *
 * A **promise** is cached rather than a value, so two screens mounting together share one
 * request instead of racing to make two. A failure is not cached — the slot is cleared so the
 * next caller retries rather than inheriting an error for the rest of the session.
 *
 * Nothing else here is cached, deliberately. The question, option-list and survey catalogues
 * all change under the author's own hands: caching them would mean creating a list and not
 * seeing it on the next tab, which is a worse bug than the request it saves.
 */
let vocabularyOnce: Nullable<Promise<IVocabulary>> = null;

export const definitionsApi = {
  // -------------------------------------------------------------------------
  // Vocabulary
  // -------------------------------------------------------------------------

  /**
   * Everything a usage form's dropdowns need, in one read with no database behind it.
   *
   * Takes no `AbortSignal`, unlike every other read here, and the absence is the contract:
   * the request is shared between callers, so one component unmounting must not cancel a
   * fetch another is still waiting for.
   */
  vocabulary(): Promise<IVocabulary> {
    vocabularyOnce ??= apiRequest<IVocabulary>(`${BASE}/vocabulary`).catch((error: unknown) => {
      vocabularyOnce = null;
      throw error;
    });
    return vocabularyOnce;
  },

  // -------------------------------------------------------------------------
  // Surveys, paths
  // -------------------------------------------------------------------------

  /**
   * Every survey with every one of its versions — one request for a whole list screen.
   *
   * `GET /surveys/{code}/versions` exists and is deliberately not used: it answers the same
   * rows this response already nests, so reaching for it would turn one request into
   * one-per-survey for nothing.
   */
  surveys(signal?: AbortSignal): Promise<IDefinitionSurvey[]> {
    return apiRequest<IDefinitionSurvey[]>(`${BASE}/surveys`, { signal });
  },

  survey(surveyCode: string, signal?: AbortSignal): Promise<IDefinitionSurvey> {
    return apiRequest<IDefinitionSurvey>(`${BASE}/surveys/${surveyCode}`, { signal });
  },

  createSurvey(body: ICreateSurvey): Promise<IDefinitionSurvey> {
    return apiRequest<IDefinitionSurvey>(`${BASE}/surveys`, { method: 'POST', body });
  },

  renameSurvey(surveyCode: string, name: string): Promise<IDefinitionSurvey> {
    return apiRequest<IDefinitionSurvey>(`${BASE}/surveys/${surveyCode}`, {
      method: 'PUT',
      body: { name }
    });
  },

  paths(surveyCode: string, signal?: AbortSignal): Promise<ISurveyPath[]> {
    return apiRequest<ISurveyPath[]>(`${BASE}/surveys/${surveyCode}/paths`, { signal });
  },

  createPath(surveyCode: string, body: ISurveyPath): Promise<ISurveyPath> {
    return apiRequest<ISurveyPath>(`${BASE}/surveys/${surveyCode}/paths`, {
      method: 'POST',
      body
    });
  },

  updatePath(surveyCode: string, pathCode: string, body: ISurveyPath): Promise<ISurveyPath> {
    return apiRequest<ISurveyPath>(`${BASE}/surveys/${surveyCode}/paths/${pathCode}`, {
      method: 'PUT',
      body
    });
  },

  async deletePath(surveyCode: string, pathCode: string): Promise<void> {
    await apiRequest<void>(`${BASE}/surveys/${surveyCode}/paths/${pathCode}`, {
      method: 'DELETE'
    });
  },

  // -------------------------------------------------------------------------
  // Versions — lifecycle
  // -------------------------------------------------------------------------

  /**
   * One version in full — sections, usages, rules and bindings, plus the `revision` every
   * mutation below it needs.
   *
   * Fetched on demand rather than per row: this is the whole definition, and RIA's runs to
   * ~110 KB across 158 usages.
   */
  version(surveyVersionId: string, signal?: AbortSignal): Promise<IVersionDocument> {
    return apiRequest<IVersionDocument>(`${BASE}/versions/${surveyVersionId}`, { signal });
  },

  /**
   * A new draft. `source` is **required** — `'empty'` or `'clone'`, and `'clone'` needs a
   * `fromVersion`. Verified against a running server: `{}` answers 400 naming `source`.
   *
   * Answers the **version row**, not the version document: no sections, no usages, no
   * revision-bearing content. A caller that wants the document reads it back.
   */
  createVersion(surveyCode: string, body: ICreateVersion): Promise<IDefinitionVersion> {
    return apiRequest<IDefinitionVersion>(`${BASE}/surveys/${surveyCode}/versions`, {
      method: 'POST',
      body
    });
  },

  setVersionNotes(
    surveyVersionId: string,
    revision: number,
    notes: Nullable<string>
  ): Promise<IVersionDocument> {
    return apiRequest<IVersionDocument>(`${BASE}/versions/${surveyVersionId}`, {
      method: 'PUT',
      body: { notes },
      headers: ifMatch(revision)
    });
  },

  /** Discards a **draft**. A published version is frozen and answers 409. */
  async deleteVersion(surveyVersionId: string, revision: number): Promise<void> {
    await apiRequest<void>(`${BASE}/versions/${surveyVersionId}`, {
      method: 'DELETE',
      headers: ifMatch(revision)
    });
  },

  /**
   * The dry run — every finding a publish would raise, with nothing written.
   *
   * It does **not** materialize, which has two visible consequences worth knowing before
   * reading its output: it reports `not_materialized` for every inherited column, and it can
   * never report `pin_unresolvable`.
   */
  validate(surveyVersionId: string, revision: number): Promise<IValidationResult> {
    return apiRequest<IValidationResult>(`${BASE}/versions/${surveyVersionId}/validation`, {
      method: 'POST',
      headers: ifMatch(revision)
    });
  },

  publish(surveyVersionId: string, revision: number): Promise<IPublicationResult> {
    return apiRequest<IPublicationResult>(`${BASE}/versions/${surveyVersionId}/publication`, {
      method: 'POST',
      headers: ifMatch(revision)
    });
  },

  retire(surveyVersionId: string, revision: number): Promise<IPublicationResult> {
    return apiRequest<IPublicationResult>(`${BASE}/versions/${surveyVersionId}/retirement`, {
      method: 'POST',
      headers: ifMatch(revision)
    });
  },

  /**
   * A version rendered as the respondent runtime would receive it — the same snapshot model
   * the respondent endpoint serves, so the existing form renderer draws it.
   *
   * `cycleCode` decides which cycle's config is resolved, and it matters more than it looks:
   * without one the server substitutes a synthetic `(preview)` cycle whose config is empty,
   * so every `max_from: cycle.current_year` resolves to no upper bound at all and
   * `year_labels` come back absent — a multi-year table then heads its columns with raw
   * offsets. Passing the real cycle is what makes this the walkthrough ADM-07 asks for.
   *
   * An unknown code answers 400 rather than falling back silently, so a caller cannot
   * mistake "your cycle was ignored" for "your cycle was used".
   */
  preview(
    surveyVersionId: string,
    cycleCode?: Nullable<string>,
    signal?: AbortSignal
  ): Promise<IDefinitionPreview> {
    const query = cycleCode ? `?cycle=${encodeURIComponent(cycleCode)}` : '';
    return apiRequest<IDefinitionPreview>(
      `${BASE}/versions/${surveyVersionId}/preview${query}`,
      { signal }
    );
  },

  // -------------------------------------------------------------------------
  // Versions — sections
  // -------------------------------------------------------------------------

  createSection(
    surveyVersionId: string,
    revision: number,
    body: ISectionWrite
  ): Promise<IDefinitionSection> {
    return apiRequest<IDefinitionSection>(`${BASE}/versions/${surveyVersionId}/sections`, {
      method: 'POST',
      body,
      headers: ifMatch(revision)
    });
  },

  updateSection(
    surveyVersionId: string,
    revision: number,
    sectionCode: string,
    body: ISectionWrite
  ): Promise<IDefinitionSection> {
    return apiRequest<IDefinitionSection>(
      `${BASE}/versions/${surveyVersionId}/sections/${sectionCode}`,
      { method: 'PUT', body, headers: ifMatch(revision) }
    );
  },

  async deleteSection(
    surveyVersionId: string,
    revision: number,
    sectionCode: string
  ): Promise<void> {
    await apiRequest<void>(`${BASE}/versions/${surveyVersionId}/sections/${sectionCode}`, {
      method: 'DELETE',
      headers: ifMatch(revision)
    });
  },

  /** The whole order, as codes. A partial list is not a partial move — the server takes
   *  this as the section order. */
  async reorderSections(
    surveyVersionId: string,
    revision: number,
    order: string[]
  ): Promise<void> {
    await apiRequest<void>(`${BASE}/versions/${surveyVersionId}/sections/order`, {
      method: 'PUT',
      body: { order },
      headers: ifMatch(revision)
    });
  },

  // -------------------------------------------------------------------------
  // Versions — usages
  // -------------------------------------------------------------------------

  createUsage(
    surveyVersionId: string,
    revision: number,
    body: IUsageWrite
  ): Promise<IDefinitionUsage> {
    return apiRequest<IDefinitionUsage>(`${BASE}/versions/${surveyVersionId}/usages`, {
      method: 'POST',
      body,
      headers: ifMatch(revision)
    });
  },

  updateUsage(
    surveyVersionId: string,
    revision: number,
    usageCode: string,
    body: IUsageWrite
  ): Promise<IDefinitionUsage> {
    return apiRequest<IDefinitionUsage>(
      `${BASE}/versions/${surveyVersionId}/usages/${usageCode}`,
      { method: 'PUT', body, headers: ifMatch(revision) }
    );
  },

  async deleteUsage(
    surveyVersionId: string,
    revision: number,
    usageCode: string
  ): Promise<void> {
    await apiRequest<void>(`${BASE}/versions/${surveyVersionId}/usages/${usageCode}`, {
      method: 'DELETE',
      headers: ifMatch(revision)
    });
  },

  /** Usage order is **per section**, which is why this takes one — the version has no single
   *  flat order to replace. */
  async reorderUsages(
    surveyVersionId: string,
    revision: number,
    section: Nullable<string>,
    order: string[]
  ): Promise<void> {
    await apiRequest<void>(`${BASE}/versions/${surveyVersionId}/usages/order`, {
      method: 'PUT',
      body: { section, order },
      headers: ifMatch(revision)
    });
  },

  // -------------------------------------------------------------------------
  // Versions — rules
  // -------------------------------------------------------------------------

  /**
   * The rule row **only** — code, predicate, priority, notes.
   *
   * Effects are **not** part of this body and an `effects` array sent here is silently
   * ignored by the binder (verified: a create carrying one answered 201 with
   * `"effects": []`). They are their own whole-block write, `setRuleEffects` below. A rule
   * created here and left alone is inert, and the dry run says so — `rule_without_effects`,
   * plus `rule_predicate_missing` when it also has no predicate, because a predicate-less
   * rule must carry exactly one `set_state` effect.
   */
  createRule(
    surveyVersionId: string,
    revision: number,
    body: IRuleWrite
  ): Promise<IDefinitionRule> {
    return apiRequest<IDefinitionRule>(`${BASE}/versions/${surveyVersionId}/rules`, {
      method: 'POST',
      body,
      headers: ifMatch(revision)
    });
  },

  updateRule(
    surveyVersionId: string,
    revision: number,
    ruleCode: string,
    body: IRuleWrite
  ): Promise<IDefinitionRule> {
    return apiRequest<IDefinitionRule>(`${BASE}/versions/${surveyVersionId}/rules/${ruleCode}`, {
      method: 'PUT',
      body,
      headers: ifMatch(revision)
    });
  },

  /**
   * The rule's whole ordered effect list, replaced.
   *
   * Separate from the rule write because the ordinals are a set, not positions: N round
   * trips would leave intermediate states carrying duplicate ordinals. An empty array — or
   * `{}` — clears the list, which is what a whole block means. The server numbers what it
   * receives and re-mints every effect id, so the read's `effectId` and `ordinal` are not
   * sent back; the binder ignores them if they are, which is what makes a read document a
   * legal write body here.
   */
  setRuleEffects(
    surveyVersionId: string,
    revision: number,
    ruleCode: string,
    effects: IRuleEffectWrite[]
  ): Promise<IDefinitionRule> {
    return apiRequest<IDefinitionRule>(
      `${BASE}/versions/${surveyVersionId}/rules/${ruleCode}/effects`,
      { method: 'PUT', body: { effects }, headers: ifMatch(revision) }
    );
  },

  async deleteRule(
    surveyVersionId: string,
    revision: number,
    ruleCode: string
  ): Promise<void> {
    await apiRequest<void>(`${BASE}/versions/${surveyVersionId}/rules/${ruleCode}`, {
      method: 'DELETE',
      headers: ifMatch(revision)
    });
  },

  // -------------------------------------------------------------------------
  // The bank
  // -------------------------------------------------------------------------

  /** The shared question bank. `code` is a **prefix** filter, not a search. */
  questions(prefix?: string, signal?: AbortSignal): Promise<IBankQuestionRow[]> {
    return apiRequest<IBankQuestionRow[]>(`${BASE}/questions${codePrefix(prefix)}`, { signal });
  },

  question(questionCode: string, signal?: AbortSignal): Promise<IBankQuestionRow> {
    return apiRequest<IBankQuestionRow>(`${BASE}/questions/${questionCode}`, { signal });
  },

  createQuestion(body: IBankQuestionWrite): Promise<IBankQuestionRow> {
    return apiRequest<IBankQuestionRow>(`${BASE}/questions`, { method: 'POST', body });
  },

  updateQuestion(questionCode: string, body: IBankQuestionWrite): Promise<IBankQuestionRow> {
    return apiRequest<IBankQuestionRow>(`${BASE}/questions/${questionCode}`, {
      method: 'PUT',
      body
    });
  },

  /** Refused with a 409 while any version still places this question. */
  async deleteQuestion(questionCode: string): Promise<void> {
    await apiRequest<void>(`${BASE}/questions/${questionCode}`, { method: 'DELETE' });
  },

  // -------------------------------------------------------------------------
  // Option lists
  // -------------------------------------------------------------------------

  optionLists(prefix?: string, signal?: AbortSignal): Promise<IOptionListRow[]> {
    return apiRequest<IOptionListRow[]>(`${BASE}/option-lists${codePrefix(prefix)}`, { signal });
  },

  optionList(listCode: string, signal?: AbortSignal): Promise<IOptionListRow> {
    return apiRequest<IOptionListRow>(`${BASE}/option-lists/${listCode}`, { signal });
  },

  createOptionList(body: { code: string; name: string }): Promise<IOptionListRow> {
    return apiRequest<IOptionListRow>(`${BASE}/option-lists`, { method: 'POST', body });
  },

  renameOptionList(listCode: string, name: string): Promise<IOptionListRow> {
    return apiRequest<IOptionListRow>(`${BASE}/option-lists/${listCode}`, {
      method: 'PUT',
      body: { name }
    });
  },

  /** Every version of a list **with its membership** — this is the read whose entries are
   *  legal `PUT …/items` bodies, which is why the catalogue read does not carry items. */
  optionListVersions(
    listCode: string,
    signal?: AbortSignal
  ): Promise<IOptionListVersionDetail[]> {
    return apiRequest<IOptionListVersionDetail[]>(`${BASE}/option-lists/${listCode}/versions`, {
      signal
    });
  },

  createOptionListVersion(
    listCode: string,
    body: ICreateOptionListVersion = {}
  ): Promise<IOptionListVersionDetail> {
    return apiRequest<IOptionListVersionDetail>(`${BASE}/option-lists/${listCode}/versions`, {
      method: 'POST',
      body
    });
  },

  /** Publishes a **draft** list version. Its membership freezes at that moment, which is
   *  what makes an unpinned usage's late-bound resolution meaningful. */
  publishOptionListVersion(
    listCode: string,
    versionNo: number
  ): Promise<IOptionListVersionDetail> {
    return apiRequest<IOptionListVersionDetail>(
      `${BASE}/option-lists/${listCode}/versions/${versionNo}/publication`,
      { method: 'POST' }
    );
  },

  retireOptionListVersion(
    listCode: string,
    versionNo: number
  ): Promise<IOptionListVersionDetail> {
    return apiRequest<IOptionListVersionDetail>(
      `${BASE}/option-lists/${listCode}/versions/${versionNo}/retirement`,
      { method: 'POST' }
    );
  },

  async deleteOptionListVersion(listCode: string, versionNo: number): Promise<void> {
    await apiRequest<void>(`${BASE}/option-lists/${listCode}/versions/${versionNo}`, {
      method: 'DELETE'
    });
  },

  /**
   * The whole ordered membership. Not a patch: `{ items: [] }` clears the list, and the
   * server numbers the array from the order it arrives in, so `ordinal` is not sent.
   *
   * Refused on a **published** version — an `option_list_version` past draft is frozen for
   * the same reason a survey version is.
   */
  setOptionListItems(
    listCode: string,
    versionNo: number,
    items: IOptionListItemWrite[]
  ): Promise<IOptionListVersionDetail> {
    return apiRequest<IOptionListVersionDetail>(
      `${BASE}/option-lists/${listCode}/versions/${versionNo}/items`,
      { method: 'PUT', body: { items } }
    );
  }
};
